import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Radio, Gift, Loader2, Target, Heart, Sparkles, Clock, Flag, Users, Crown } from "lucide-react";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { goalPct, milestoneMsg, highestMilestone, deadlineInfo, MILESTONES, money } from "@/lib/tipgoal";

const PRESETS = [5, 10, 25, 50];

function timeAgo(iso) {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

function fireConfetti() {
  const colors = ["#34d399", "#fbbf24", "#a78bfa"];
  confetti({ particleCount: 90, spread: 70, origin: { y: 0.35 }, colors });
  setTimeout(() => confetti({ particleCount: 50, angle: 60, spread: 55, origin: { x: 0, y: 0.5 }, colors }), 150);
  setTimeout(() => confetti({ particleCount: 50, angle: 120, spread: 55, origin: { x: 1, y: 0.5 }, colors }), 150);
}

export default function TipPage() {
  const { artistId } = useParams();
  const [card, setCard] = useState(null);
  const [tippers, setTippers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState(10);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const celebrated = useRef(false);

  useEffect(() => {
    api.get(`/tip-card/${artistId}`).then(({ data }) => setCard(data)).catch(() => setCard(null)).finally(() => setLoading(false));
    api.get(`/tip-card/${artistId}/tippers`).then(({ data }) => setTippers(data)).catch(() => {});
  }, [artistId]);

  const goal = card?.tip_goal_cents || 0;
  const progress = card?.tip_progress_cents || 0;
  const pct = goalPct(progress, goal);
  const dl = deadlineInfo(card?.tip_goal_deadline);
  const ended = card?.goal_ended || dl?.ended;
  const shout = milestoneMsg(pct);

  // Celebrate a milestone crossed since the viewer last visited
  useEffect(() => {
    if (!card || !goal || celebrated.current) return;
    celebrated.current = true;
    const key = `tipseen_${artistId}`;
    const prev = Number(localStorage.getItem(key) || 0);
    const cur = highestMilestone(pct);
    if (cur > prev && cur >= 25) {
      fireConfetti();
    }
    localStorage.setItem(key, String(cur));
  }, [card, goal, pct, artistId]);

  const sendTip = async () => {
    if (!Number(amount) || Number(amount) < 1) { toast.error("Enter at least $1"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/tips/guest-checkout", {
        artist_id: artistId, amount: Number(amount), name: name.trim(), origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start tip"); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0E] text-white flex flex-col">
      <header className="px-6 h-16 flex items-center">
        <Link to="/" className="flex items-center gap-2" data-testid="tip-brand-logo">
          <span className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center"><Radio className="w-5 h-5 text-emerald-400" /></span>
          <span className="font-display font-extrabold text-lg tracking-tight">NEXT<span className="text-emerald-400">UP</span></span>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        {loading ? (
          <Loader2 className="w-7 h-7 animate-spin text-emerald-400" />
        ) : !card ? (
          <p className="text-zinc-500 font-mono-nu" data-testid="tip-not-found">Artist not found.</p>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-lg nu-card overflow-hidden" data-testid="tip-page">
            <div className="h-28 relative">
              {card.cover_url ? <img src={mediaUrl(card.cover_url)} alt="" className="w-full h-full object-cover opacity-70" />
                : <div className="w-full h-full bg-gradient-to-br from-violet-600/40 to-emerald-600/20" />}
              <div className="absolute inset-0 bg-gradient-to-t from-[#181824] to-transparent" />
            </div>
            <div className="px-6 pb-6 -mt-10 relative">
              {card.avatar_url ? <img src={mediaUrl(card.avatar_url)} alt="" className="w-20 h-20 rounded-full object-cover ring-2 ring-emerald-500/70" />
                : <span className="w-20 h-20 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-300 font-bold text-2xl ring-2 ring-emerald-500/70">{card.artist_name?.[0]?.toUpperCase() || "A"}</span>}
              <h1 className="mt-3 font-display font-black text-2xl" data-testid="tip-artist-name">{card.artist_name}</h1>
              <p className="text-zinc-500 font-mono-nu text-sm">@{card.handle}</p>

              {goal > 0 && (
                <div className="mt-5 p-4 rounded-xl bg-black/40 border border-white/10" data-testid="tip-page-goal">
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2 text-sm font-semibold"><Target className="w-4 h-4 text-emerald-400" /> {card.tip_goal_label || "Tip Goal"}</span>
                    <span className="text-xs font-mono-nu text-zinc-400">{money(progress)} <span className="text-zinc-600">/ {money(goal)}</span></span>
                  </div>
                  <div className="mt-3 relative h-2.5 rounded-full bg-white/5 overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-700 ${pct >= 100 ? "bg-amber-400" : "bg-gradient-to-r from-emerald-500 to-emerald-400"}`} style={{ width: `${pct}%` }} />
                    {MILESTONES.slice(0, 3).map((m) => <span key={m} className="absolute top-0 bottom-0 w-px bg-black/50" style={{ left: `${m}%` }} />)}
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-xs text-zinc-500 flex items-center gap-1" data-testid="tip-milestone">
                      {ended ? <><Flag className="w-3.5 h-3.5" /> Goal ended</> : shout ? <><Sparkles className="w-3.5 h-3.5 text-emerald-400" /> {shout}</> : `${pct}% there this month`}
                    </p>
                    {dl && !ended && <span className={`text-[11px] font-mono-nu flex items-center gap-1 ${dl.daysLeft <= 2 ? "text-amber-400" : "text-zinc-500"}`} data-testid="tip-deadline"><Clock className="w-3 h-3" /> {dl.label}</span>}
                  </div>
                </div>
              )}

              {ended ? (
                <div className="mt-5 text-sm text-zinc-400 bg-black/30 border border-white/10 rounded-xl p-4 flex items-center gap-2" data-testid="tip-goal-ended">
                  <Flag className="w-4 h-4 text-zinc-500" /> This tip goal has ended. Thanks to everyone who chipped in!
                </div>
              ) : !card.can_receive ? (
                <p className="mt-5 text-sm text-zinc-400 bg-black/30 border border-white/10 rounded-xl p-4" data-testid="tip-not-ready">
                  This artist hasn't set up payouts yet, so tips can't be sent right now. Check back soon!
                </p>
              ) : (
                <>
                  <p className="mt-6 text-sm text-zinc-300 flex items-center gap-2"><Gift className="w-4 h-4 text-emerald-400" /> Send a tip — no account needed</p>
                  <div className="mt-3 grid grid-cols-4 gap-2">
                    {PRESETS.map((p) => (
                      <button key={p} onClick={() => setAmount(p)} data-testid={`tip-preset-${p}`}
                        className={`py-2.5 rounded-xl font-bold text-sm transition-colors ${Number(amount) === p ? "bg-emerald-500 text-black" : "bg-black/40 border border-white/10 hover:border-emerald-500/50"}`}>
                        ${p}
                      </button>
                    ))}
                  </div>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-zinc-400">Amount (USD)</label>
                      <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="tip-amount"
                        className="w-full mt-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-emerald-500/50" />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400">Your name (optional)</label>
                      <input type="text" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} data-testid="tip-name"
                        placeholder="Anonymous" className="w-full mt-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-emerald-500/50" />
                    </div>
                  </div>
                  <button onClick={sendTip} disabled={busy} data-testid="tip-send-btn"
                    className="mt-4 w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-black font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Heart className="w-5 h-5" /> Tip {money(Math.round((Number(amount) || 0) * 100))}</>}
                  </button>
                  <p className="mt-3 text-[11px] text-zinc-600 text-center">Secure checkout by Stripe · {card.artist_name} keeps 90%</p>
                </>
              )}

              {/* Recent tippers */}
              <div className="mt-6 pt-5 border-t border-white/5" data-testid="recent-tippers">
                <p className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-emerald-400" /> Recent supporters</p>
                {card.top_supporter && (
                  <div className="mb-3 flex items-center justify-between gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30" data-testid="top-supporter">
                    <span className="flex items-center gap-2 min-w-0">
                      <Crown className="w-4 h-4 text-amber-400 shrink-0" />
                      <span className="text-sm font-semibold truncate">{card.top_supporter.name}</span>
                      <span className="text-xs text-amber-400/80 shrink-0">· Top supporter</span>
                    </span>
                    <span className="font-mono-nu text-amber-400 shrink-0">{money(card.top_supporter.amount_cents)}</span>
                  </div>
                )}
                {tippers.length === 0 ? (
                  <p className="text-xs text-zinc-500">Be the first to support {card.artist_name}! 💚</p>
                ) : (
                  <div className="space-y-2">
                    {tippers.map((t, i) => {
                      const isTop = card.top_supporter && t.name === card.top_supporter.name;
                      return (
                        <div key={i} className="flex items-center justify-between text-sm" data-testid={`tipper-${i}`}>
                          <span className="flex items-center gap-2 min-w-0">
                            <span className="w-7 h-7 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-300 text-xs font-bold shrink-0">{(t.name || "A")[0].toUpperCase()}</span>
                            <span className="truncate text-zinc-300 flex items-center gap-1">{t.name}{isTop && <Crown className="w-3 h-3 text-amber-400" />}</span>
                            <span className="text-xs text-zinc-600 shrink-0">· {timeAgo(t.created_at)}</span>
                          </span>
                          <span className="font-mono-nu text-emerald-400 shrink-0">{money(t.amount_cents)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
